import React, { useState, useEffect } from 'react';
import './RecapLinkModal.css';
import DeliveryPartnerSearch from './DeliveryPartnerSearch';
import AmountInput from './ui/AmountInput';
import { QUANTITY_DECIMALS } from '../utils/precision';
import { canonicalizeAmount } from '../utils/amountFormat';
import { formatQuantityDisplay } from '../utils/quantityDisplay';
import { hasSeveralProducts } from '../utils/dealProductLines';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faSquare, faPlus, faBalanceScale, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

/**
 * RecapLinkModal Component
 * 
 * Modal for linking existing Purchase or Sales recaps to a delivery.
 * Supports multi-selection - allows user to select multiple recaps and link them all at once.
 * Pre-selects recaps that are already linked to the delivery.
 * Also supports creating new blank recaps.
 */
const RecapLinkModal = ({ 
  isOpen, 
  onClose, 
  recapType, // 'purchase' or 'sales'
  availableRecaps = [], 
  linkedRecaps = [], // Already linked recaps to pre-select
  onLink,
  onCreateBlank, // NEW: Handler for creating blank recaps
  units = [] // Units array for display
}) => {
  const [selectedRecaps, setSelectedRecaps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // PARTIAL ALLOCATION FEATURE (UPDATED - Per-Recap Basis)
  const [partialRecaps, setPartialRecaps] = useState({});  // {recapId: true/false} - tracks which recaps have Partial checked
  const [recapQuantities, setRecapQuantities] = useState({});  // {recapId: quantity}
  const [quantityErrors, setQuantityErrors] = useState({});  // {recapId: errorMessage}
  
  // BLANK RECAP PARTNER SELECTION
  const [showPartnerSelection, setShowPartnerSelection] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);

  // Extract base product name (before first "|" or full name)
  const extractBaseProduct = (productString) => {
    if (!productString) return null;
    const parts = productString.split('|').map(p => p.trim());
    return parts[0].toLowerCase(); // "Steam Coal | Australia" → "steam coal"
  };

  // Get unit display name from unit ID or code
  const getUnitName = (unitValue) => {
    if (!unitValue) return 'N/A';
    // Find unit by ID or code
    const unit = units.find(u => String(u.id) === String(unitValue) || u.code === String(unitValue));
    return unit ? unit.name : String(unitValue);
  };

  // Get currency from any recap (purchase or sales)
  const getRecapCurrency = (recap) => {
    return recap.purchase_contract_price_currency || 
           recap.sale_contract_price_currency || 
           recap.price_currency;
  };
  
  // Get unit from any recap (purchase or sales)
  const getRecapUnit = (recap) => {
    return recap.purchase_contract_quantity_unit || 
           recap.sale_contract_quantity_unit || 
           recap.quantity_unit;
  };

  // Check if a recap is compatible with already linked recaps (CROSS-TYPE)
  const checkRecapCompatibility = (recap, linkedRecaps) => {
    if (linkedRecaps.length === 0) return true;
    
    const first = linkedRecaps[0];
    
    // Extract values from both recaps (works for purchase OR sales)
    const baseProductMatch = extractBaseProduct(recap.product) === extractBaseProduct(first.product);
    const currencyMatch = getRecapCurrency(recap) === getRecapCurrency(first);
    const unitMatch = getRecapUnit(recap) === getRecapUnit(first);
    
    return baseProductMatch && currencyMatch && unitMatch;
  };


  // Log when modal opens/closes and initialize with linked recaps
  useEffect(() => {
////    console.log('🔔 RecapLinkModal - isOpen changed:', isOpen);
    if (isOpen) {
////      console.log('  📦 Modal Type:', recapType);
////      console.log('  📊 Available Recaps:', availableRecaps);
////      console.log('  📈 Recaps Count:', availableRecaps?.length || 0);
////      console.log('  🔗 Linked Recaps:', linkedRecaps);
      
      if (availableRecaps && availableRecaps.length > 0) {
////        console.log('  ✅ First recap:', availableRecaps[0]);
////        console.log('  🏷️ First recap display name:', availableRecaps[0].recap_display_name);
////        console.log('  🔢 First recap transaction number:', availableRecaps[0].transaction_number);
      } else {
////        console.log('  ⚠️ No recaps available');
      }
      
      // Pre-select already linked recaps
      if (linkedRecaps && linkedRecaps.length > 0) {
        const linkedRecapIds = linkedRecaps.map(r => r.id);
        const preSelectedRecaps = availableRecaps.filter(r => linkedRecapIds.includes(r.id));
////        console.log('  🎯 Pre-selecting linked recaps:', preSelectedRecaps);
        setSelectedRecaps(preSelectedRecaps);
      }
    }
    
    if (!isOpen) {
      setSelectedRecaps([]);
      setPartialRecaps({});
      setRecapQuantities({});
      setQuantityErrors({});
      setShowPartnerSelection(false);
      setSelectedPartner('');
      setSelectedPartnerId(null);
    }
  }, [isOpen, recapType, availableRecaps, linkedRecaps]);
  
  // Log when availableRecaps prop changes
  useEffect(() => {
////    console.log('📋 RecapLinkModal - availableRecaps prop changed:', {
////      type: recapType,
////      count: availableRecaps?.length || 0,
////      recaps: availableRecaps
////    });
  }, [availableRecaps, recapType]);

  const handleLink = async () => {
    if (selectedRecaps.length === 0) return;
    
    // FINAL VALIDATION: Ensure all selected recaps are compatible with each other
    if (selectedRecaps.length > 1) {
      const firstRecap = selectedRecaps[0];
      for (let i = 1; i < selectedRecaps.length; i++) {
        if (!checkRecapCompatibility(selectedRecaps[i], [firstRecap])) {
          setValidationError('Selected recaps have mismatched products, units, or currencies. Please select compatible recaps only.');
          return;
        }
      }
    }
    
    // CROSS-CHECK: Validate selected recaps against already linked recaps
    if (linkedRecaps.length > 0 && selectedRecaps.length > 0) {
      for (const selectedRecap of selectedRecaps) {
        if (!checkRecapCompatibility(selectedRecap, linkedRecaps)) {
          setValidationError('Selected recaps do not match already linked recaps. All recaps must have the same product, unit, and currency.');
          return;
        }
      }
    }
    
    // PARTIAL ALLOCATION VALIDATION (for recaps with Partial checked)
    let hasErrors = false;
    for (const recap of selectedRecaps) {
      // Only validate if this recap has Partial checked
      if (partialRecaps[recap.id]) {
        const qty = recapQuantities[recap.id];
        if (!qty || parseFloat(qty) <= 0) {
          setQuantityErrors(prev => ({
            ...prev,
            [recap.id]: 'Quantity must be greater than 0'
          }));
          hasErrors = true;
        }
      }
    }
    
    if (hasErrors) {
      setValidationError('Please specify valid quantities for all partial recaps');
      return;
    }
    
    setIsLoading(true);
    try {
      // Get IDs of already linked recaps to avoid re-linking
      const linkedRecapIds = linkedRecaps.map(r => r.id);
      
      // Link only newly selected recaps (not already linked)
      const recapsToLink = selectedRecaps.filter(r => !linkedRecapIds.includes(r.id));
      
      // Link all new recaps sequentially
      for (const recap of recapsToLink) {
        // Pass allocated_quantity if this recap has Partial checked, otherwise null (= full quantity)
        const allocatedQuantity = partialRecaps[recap.id] ? recapQuantities[recap.id] : null;
        await onLink(recap.id, allocatedQuantity);
      }
      
      onClose();
    } catch (error) {
      console.error('Error linking recaps:', error);
      setValidationError('Failed to link recaps. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // State for validation errors
  const [validationError, setValidationError] = useState('');
  
  // Toggle selection of a recap
  const toggleRecapSelection = (recap) => {
    setSelectedRecaps(prev => {
      const isSelected = prev.some(r => r.id === recap.id);
      if (isSelected) {
        // Deselecting - clear any error
        setValidationError('');
        return prev.filter(r => r.id !== recap.id);
      } else {
        // Selecting - validate against BOTH already selected AND already linked recaps
        const allBaselineRecaps = [...prev, ...linkedRecaps];
        
        if (allBaselineRecaps.length > 0) {
          const isCompatible = checkRecapCompatibility(recap, allBaselineRecaps);
          if (!isCompatible) {
            const firstRecap = allBaselineRecaps[0];
            const isPurchase = recapType === 'purchase';
            
            // Extract mismatch details
            const firstProduct = extractBaseProduct(firstRecap.product);
            const newProduct = extractBaseProduct(recap.product);
            const firstCurrency = getRecapCurrency(firstRecap);
            const newCurrency = getRecapCurrency(recap);
            const firstUnit = getRecapUnit(firstRecap);
            const newUnit = getRecapUnit(recap);
            
            // Build specific error message
            let errorMsg = `Cannot select ${recap.transaction_number}: `;
            if (firstProduct !== newProduct) {
              errorMsg += `Product mismatch (expected: ${firstProduct}, got: ${newProduct})`;
            } else if (firstCurrency !== newCurrency) {
              errorMsg += `Currency mismatch (expected: ${firstCurrency}, got: ${newCurrency})`;
            } else if (firstUnit !== newUnit) {
              errorMsg += `Unit mismatch (expected: ${firstUnit}, got: ${newUnit})`;
            }
            
            setValidationError(errorMsg);
            return prev; // Don't add to selection
          }
        }
        
        // Clear any previous error
        setValidationError('');
        return [...prev, recap];
      }
    });
  };
  
  // Check if a recap is selected
  const isRecapSelected = (recapId) => {
    return selectedRecaps.some(r => r.id === recapId);
  };
  
  // Check if a recap is already linked
  const isRecapLinked = (recapId) => {
    return linkedRecaps.some(r => r.id === recapId);
  };
  
  // Handle create blank recap - show partner selection first
  const handleCreateBlank = () => {
    setShowPartnerSelection(true);
  };
  
  // Handle partner selection confirmation
  const handlePartnerConfirm = () => {
    if (!selectedPartner || !selectedPartnerId) {
      setValidationError(`Please select a ${recapType === 'purchase' ? 'seller' : 'buyer'}`);
      return;
    }
    
    if (onCreateBlank) {
      // Pass partner info to the create blank handler
      onCreateBlank(selectedPartner, selectedPartnerId);
      onClose();
    }
  };
  
  // Handle back from partner selection
  const handlePartnerBack = () => {
    setShowPartnerSelection(false);
    setSelectedPartner('');
    setSelectedPartnerId(null);
    setValidationError('');
  };

  if (!isOpen) return null;

  const isPurchase = recapType === 'purchase';
  const title = isPurchase ? 'Link Purchase Recaps' : 'Link Sales Recaps';
  const noDataMessage = isPurchase 
    ? 'No available purchase recaps found' 
    : 'No available sales recaps found';
  const selectedCount = selectedRecaps.length;
  
  // Calculate how many are new vs already linked
  const linkedRecapIds = linkedRecaps.map(r => r.id);
  const newRecapsCount = selectedRecaps.filter(r => !linkedRecapIds.includes(r.id)).length;

  return (
    <div className="recap-link-modal-overlay" onClick={onClose}>
      <div className="recap-link-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="recap-link-modal-header">
          <h2>{title}</h2>
          {selectedCount > 0 && (
            <span className="recap-link-selected-badge">
              {selectedCount} selected
            </span>
          )}
          <button 
            className="recap-link-modal-close" 
            onClick={onClose}
            type="button"
          >
            &times;
          </button>
        </div>

        <div className="recap-link-modal-body">
          {/* Partner Selection View */}
          {showPartnerSelection ? (
            <div className="recap-partner-selection-view">
              <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '600' }}>
                Select {isPurchase ? 'Seller' : 'Buyer'} for New Blank Recap
              </div>
              
              {/* Validation Error Display */}
              {validationError && (
                <div style={{
                  padding: '12px',
                  marginBottom: '15px',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #dc2626',
                  borderRadius: '4px',
                  color: '#dc2626',
                  fontSize: '14px'
                }}>
                  {validationError}
                </div>
              )}
              
              <DeliveryPartnerSearch
                label={`${isPurchase ? 'Seller' : 'Buyer'} (Required)`}
                fieldName={isPurchase ? 'seller' : 'buyer'}
                fieldNameId={isPurchase ? 'seller_crm_id' : 'buyer_crm_id'}
                value={selectedPartner}
                onChange={(fieldName, fieldNameId, partnerId, partnerName) => {
                  setSelectedPartner(partnerName);
                  setSelectedPartnerId(partnerId);
                  setValidationError('');
                }}
                required={true}
              />
              
              <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '14px', color: '#666' }}>
                <strong>Note:</strong> This {isPurchase ? 'seller' : 'buyer'} will be automatically added to the delivery's WHO section contractual list.
              </div>
            </div>
          ) : (
            <>
              {/* Consistency Requirements Info */}
              {linkedRecaps.length > 0 && (
                <div className="recap-requirements-info">
                  <div className="recap-requirements-title">Requirements for Additional Recaps:</div>
                  <div className="recap-requirements-list">
                  <div className="recap-requirement-item">
                    Product: <strong>{extractBaseProduct(linkedRecaps[0].product)}</strong>
                  </div>
                  <div className="recap-requirement-item">
                    Currency: <strong>{getRecapCurrency(linkedRecaps[0])}</strong>
                  </div>
                  <div className="recap-requirement-item">
                    Unit: <strong>{getUnitName(getRecapUnit(linkedRecaps[0]))}</strong>
                  </div>
                  </div>
                </div>
              )}
              
              {/* Validation Error Display */}
              {validationError && (
                <div style={{
                  padding: '12px',
                  marginBottom: '15px',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #dc2626',
                  borderRadius: '4px',
                  color: '#dc2626',
                  fontSize: '14px'
                }}>
                  {validationError}
                </div>
              )}

              {/* Create New Blank Recap Button */}
              {onCreateBlank && (
                <button 
                  type="button"
                  className="recap-link-create-blank-btn"
                  onClick={handleCreateBlank}
                >
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
                  Create New Blank {isPurchase ? 'Purchase' : 'Sales'} Recap
                </button>
              )}
            </>
          )}
          
          {!showPartnerSelection && availableRecaps.length === 0 ? (
            <div className="recap-link-no-data">{noDataMessage}</div>
          ) : !showPartnerSelection ? (
            <div className="recap-link-list">
              {availableRecaps.map((recap) => {
                // Check if this recap is already linked
                const isLinked = isRecapLinked(recap.id);
                
                // Check compatibility against BOTH linked AND selected recaps
                const allBaselineRecaps = [...linkedRecaps, ...selectedRecaps];
                const isCompatible = allBaselineRecaps.length === 0 || checkRecapCompatibility(recap, allBaselineRecaps);
                const isSelected = isRecapSelected(recap.id);
                
                // Can select if compatible AND not already linked
                const canSelect = isCompatible && !isLinked;
                
                const fullQty = isPurchase 
                  ? recap.purchase_contract_quantity 
                  : recap.sale_contract_quantity;
                const unit = isPurchase
                  ? recap.purchase_contract_quantity_unit
                  : recap.sale_contract_quantity_unit;
                const unitName = getUnitName(unit);
                
                // The recap's contract quantity is stored as text, so it may be
                // grouped with spaces ("300 000") or commas ("1,000.5"). Both are
                // thousands separators: parseFloat stopped at the comma and read
                // "1,000.5" as 1, so "Cannot exceed" fired on any real allocation.
                const fullQtyNumeric = parseFloat(canonicalizeAmount(String(fullQty ?? '').replace(/\s/g, '')));
                // What the hint and the "too much" warning quote: every decimal the
                // recap carries (the browser default cut it to 3).
                const fullQtyDisplay = formatQuantityDisplay(String(fullQty).replace(/\s/g, ''));
                
                return (
                  <div
                    key={recap.id}
                    className={`recap-link-item ${
                      isSelected ? 'selected' : ''
                    } ${!canSelect && !isSelected ? 'incompatible' : ''} ${isLinked ? 'linked-disabled' : ''}`}
                  >
                    <div 
                      className="recap-link-item-header"
                      onClick={() => !isLinked && (canSelect || isSelected) && toggleRecapSelection(recap)}
                      style={{ cursor: isLinked ? 'default' : 'pointer' }}
                    >
                      <div className="recap-link-item-checkbox">
                        <FontAwesomeIcon 
                          icon={isRecapSelected(recap.id) ? faCheckSquare : faSquare}
                          className="recap-checkbox-icon"
                          style={{ opacity: isLinked ? 0.3 : 1 }}
                        />
                      </div>
                      <span className="recap-link-item-number">
                        {recap.display_name || recap.transaction_number}
                      </span>
                      {recap.display_name && (
                        <span className="recap-link-item-transaction-id" style={{
                          fontSize: '12px',
                          color: '#666',
                          marginLeft: '8px'
                        }}>
                          ({recap.transaction_number})
                        </span>
                      )}
                      {!canSelect && linkedRecaps.length > 0 && !isSelected && !isLinked && (
                        <span className="recap-incompatible-badge">
                          Incompatible
                        </span>
                      )}
                      {isLinked && (
                        <span className="recap-linked-badge">Already Linked</span>
                      )}
                  </div>
                  
                  <div className="recap-link-item-content">
                    <div 
                      className="recap-link-item-details-wrapper"
                      onClick={() => (canSelect || isSelected) && toggleRecapSelection(recap)}
                    >
                      <div className="recap-link-item-details">
                        {!isCompatible && !isSelected && allBaselineRecaps.length > 0 ? (
                      <>
                        <div className="recap-requirements-inline">
                          <div className="recap-requirement-inline-title">Required Values:</div>
                          <div className="recap-requirement-inline-item">
                            <strong>Product:</strong> {extractBaseProduct(allBaselineRecaps[0].product) || 'N/A'}
                          </div>
                          <div className="recap-requirement-inline-item">
                            <strong>Currency:</strong> {getRecapCurrency(allBaselineRecaps[0])}
                          </div>
                          <div className="recap-requirement-inline-item">
                            <strong>Unit:</strong> {getUnitName(getRecapUnit(allBaselineRecaps[0]))}
                          </div>
                        </div>
                        <div className="recap-current-values">
                          <div className="recap-current-values-title">Current Values:</div>
                          {isPurchase ? (
                            <>
                              <div className="recap-link-detail">
                                <span className="recap-link-label">Product:</span>
                                <span>{extractBaseProduct(recap.product) || 'N/A'}</span>
                              </div>
                              <div className="recap-link-detail">
                                <span className="recap-link-label">Currency:</span>
                                <span>{getRecapCurrency(recap) || 'N/A'}</span>
                              </div>
                              <div className="recap-link-detail">
                                <span className="recap-link-label">Unit:</span>
                                <span>{getUnitName(getRecapUnit(recap))}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="recap-link-detail">
                                <span className="recap-link-label">Product:</span>
                                <span>{extractBaseProduct(recap.product) || 'N/A'}</span>
                              </div>
                              <div className="recap-link-detail">
                                <span className="recap-link-label">Currency:</span>
                                <span>{getRecapCurrency(recap) || 'N/A'}</span>
                              </div>
                              <div className="recap-link-detail">
                                <span className="recap-link-label">Unit:</span>
                                <span>{getUnitName(getRecapUnit(recap))}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {isPurchase ? (
                          <>
                            <div className="recap-link-detail">
                              <span className="recap-link-label">Quantity:</span>
                              <span>{recap.purchase_contract_quantity || 'N/A'}</span>
                            </div>
                            <div className="recap-link-detail">
                              <span className="recap-link-label">Product:</span>
                              <span>{recap.product || 'N/A'}</span>
                            </div>
                            <div className="recap-link-detail">
                              <span className="recap-link-label">Unit:</span>
                              <span>{getUnitName(getRecapUnit(recap))}</span>
                            </div>
                            <div className="recap-link-detail">
                              <span className="recap-link-label">Currency:</span>
                              <span>{getRecapCurrency(recap) || 'N/A'}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="recap-link-detail">
                              <span className="recap-link-label">Quantity:</span>
                              <span>{recap.sale_contract_quantity || 'N/A'}</span>
                            </div>
                            <div className="recap-link-detail">
                              <span className="recap-link-label">Product:</span>
                              <span>{recap.product || 'N/A'}</span>
                            </div>
                            <div className="recap-link-detail">
                              <span className="recap-link-label">Unit:</span>
                              <span>{getUnitName(getRecapUnit(recap))}</span>
                            </div>
                            <div className="recap-link-detail">
                              <span className="recap-link-label">Currency:</span>
                              <span>{getRecapCurrency(recap) || 'N/A'}</span>
                            </div>
                          </>
                        )}
                      </>
                        )}
                      </div>
                      
                      {/* PARTIAL CHECKBOX - Only show if recap is NOT already linked. A
                          recap with several products is split per product on the
                          delivery instead (DeliveryProductsTable). */}
                      {!isLinked && !hasSeveralProducts(recap) && (
                        <div className="recap-partial-checkbox-container">
                          <label 
                            className="recap-partial-checkbox-label"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={partialRecaps[recap.id] || false}
                              onChange={(e) => {
                              const isChecked = e.target.checked;
                              setPartialRecaps(prev => ({
                                ...prev,
                                [recap.id]: isChecked
                              }));
                              
                              // Initialize quantity with full amount when enabling
                              if (isChecked) {
                                setRecapQuantities(prev => ({
                                  ...prev,
                                  [recap.id]: fullQtyNumeric
                                }));
                              } else {
                                // Clear quantity and errors when disabling
                                setRecapQuantities(prev => {
                                  const newQtys = { ...prev };
                                  delete newQtys[recap.id];
                                  return newQtys;
                                });
                                setQuantityErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors[recap.id];
                                  return newErrors;
                                });
                              }
                            }}
                              className="recap-partial-checkbox-input"
                            />
                            <FontAwesomeIcon 
                              icon={partialRecaps[recap.id] ? faCheckSquare : faSquare}
                              className="recap-partial-checkbox-icon"
                            />
                            <span className="recap-partial-checkbox-text">Partial</span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* INLINE QUANTITY INPUT - Appears when Partial is checked */}
                  {partialRecaps[recap.id] && (
                    <div className="recap-inline-quantity-section">
                      <div className="recap-inline-quantity-label">
                        Allocated Quantity (Total: {fullQty} {unitName})
                      </div>
                      <div
                        className="recap-inline-quantity-input-field"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <AmountInput
                          value={recapQuantities[recap.id] || ''}
                          maxDecimals={QUANTITY_DECIMALS}
                          onChange={(value) => {
                            setRecapQuantities(prev => ({
                              ...prev,
                              [recap.id]: value
                            }));

                            // Validate
                            if (!value || parseFloat(value) <= 0) {
                              setQuantityErrors(prev => ({
                                ...prev,
                                [recap.id]: 'Must be greater than 0'
                              }));
                            } else if (parseFloat(value) > fullQtyNumeric) {
                              setQuantityErrors(prev => ({
                                ...prev,
                                [recap.id]: `Cannot exceed ${fullQtyDisplay}`
                              }));
                            } else {
                              setQuantityErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[recap.id];
                                return newErrors;
                              });
                            }
                          }}
                          className={`recap-inline-quantity-input ${quantityErrors[recap.id] ? 'error' : ''}`}
                          placeholder={`Max: ${fullQtyDisplay}`}
                        />
                        <span className="recap-inline-quantity-unit">{unitName}</span>
                      </div>
                      {quantityErrors[recap.id] && (
                        <div className="recap-inline-quantity-error">
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '4px' }} />
                          {quantityErrors[recap.id]}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          ) : null}
          
        </div>

        <div className="recap-link-modal-footer">
          {showPartnerSelection ? (
            <>
              <button 
                className="recap-link-modal-btn-cancel" 
                onClick={handlePartnerBack}
                type="button"
              >
                Back
              </button>
              <button 
                className="recap-link-modal-btn-link" 
                onClick={handlePartnerConfirm}
                disabled={!selectedPartner || !selectedPartnerId}
                type="button"
              >
                Create Blank Recap
              </button>
            </>
          ) : (
            <>
              <button 
                className="recap-link-modal-btn-cancel" 
                onClick={onClose}
                type="button"
              >
                Cancel
              </button>
              <button 
                className="recap-link-modal-btn-link" 
                onClick={handleLink}
                disabled={selectedCount === 0 || isLoading}
                type="button"
              >
                {isLoading 
                  ? 'Linking...' 
                  : newRecapsCount === 0
                    ? 'Close'
                    : `Link ${newRecapsCount} New Recap${newRecapsCount !== 1 ? 's' : ''}`
                }
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecapLinkModal;

